/**
 * Removing an alias asks first (TMMAPP-0142 §D item 22; TMMAPI-0181 U3). The counts come from the
 * server's dry run (`DELETE …/aliases/{aliasId}?dryRun=true`), which the Aliases tab runs when Remove
 * is pressed and passes in as `check`:
 * - **Lines named:** a choice. **Keep the names** (the default, `lines=KEEP`): the lines stay on this
 *   payee and future imports aren't matched by the alias. **Re-match them** (`lines=RERESOLVE`):
 *   how many move to other payees and how many go back to the bank's text. Hand-named lines are
 *   never changed, and the dialog says so.
 * - **No lines:** a simple confirm.
 * - **The payee's last pattern alias** (the dry run's served `lastPatternAlias`, J8): removing it
 *   takes the payee out of the matching order, and the dialog says so.
 *
 * The removal's own failure shows inside the dialog (TMM-B3).
 */
import { useId, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AliasRemovalLines, AliasRemovalPreview, PayeeAlias } from '@/api/payees';
import { ConfirmDialog } from '@/components/ConfirmDialog';

/** The dry run's state, owned by the Aliases tab. */
export type AliasRemovalCheck =
  | { status: 'loading' }
  | { status: 'ready'; preview: AliasRemovalPreview }
  | { status: 'failed'; message: string };

interface AliasRemoveDialogProps {
  alias: PayeeAlias;
  check: AliasRemovalCheck;
  busy: boolean;
  error: string | null;
  onConfirm: (lines: AliasRemovalLines) => void;
  onCancel: () => void;
}

export function AliasRemoveDialog({
  alias,
  check,
  busy,
  error,
  onConfirm,
  onCancel,
}: AliasRemoveDialogProps) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<AliasRemovalLines>('KEEP');
  const choiceId = useId();
  const keepHelpId = useId();
  const rematchHelpId = useId();
  const text = alias.pattern ?? alias.payeeNorm;
  const affected = check.status === 'ready' ? check.preview.linesAffected : 0;
  const lastPattern = check.status === 'ready' && check.preview.lastPatternAlias;

  let consequence: string;
  if (check.status === 'loading') consequence = t('aliasRemove.checking');
  // A failed dry run shows as the dialog's error (an alert that takes focus, A11Y-010); the
  // consequence stays a sentence that is true either way.
  else if (check.status === 'failed') consequence = t('aliasRemove.keepHelp');
  else if (affected === 0) consequence = t('aliasRemove.none');
  else
    consequence = `${t('aliasRemove.affected', { count: affected })} ${t('aliasRemove.handNamed')}`;

  return (
    <ConfirmDialog
      title={t('aliasRemove.title', { alias: text })}
      // A polite status region: the dry run's counts replace "Checking…" while the dialog is open,
      // and a screen reader hears them (TMMAPP-0144 item 2). A failed dry run takes the region out
      // rather than changing its text, so a screen reader hears only the error alert (TMMAPP-0146
      // item 6).
      consequence={
        check.status === 'failed' ? consequence : <span role="status">{consequence}</span>
      }
      confirmLabel={t('aliasRemove.confirm')}
      destructive
      busy={busy}
      error={error ?? (check.status === 'failed' ? check.message : undefined)}
      confirmDisabled={check.status !== 'ready'}
      onConfirm={() => onConfirm(affected === 0 ? 'KEEP' : lines)}
      onCancel={onCancel}
    >
      {check.status === 'ready' && (affected > 0 || lastPattern) && (
        <Stack spacing={2}>
          {affected > 0 && (
            <FormControl disabled={busy}>
              <FormLabel id={choiceId}>{t('aliasRemove.choiceLabel')}</FormLabel>
              <RadioGroup
                aria-labelledby={choiceId}
                value={lines}
                onChange={(event) => setLines(event.target.value as AliasRemovalLines)}
              >
                <FormControlLabel
                  value="KEEP"
                  control={<Radio slotProps={{ input: { 'aria-describedby': keepHelpId } }} />}
                  label={t('aliasRemove.keep', { count: affected })}
                />
                <FormHelperText
                  id={keepHelpId}
                  sx={{ mt: 0, mb: 1, marginInlineStart: 4, marginInlineEnd: 0 }}
                >
                  {t('aliasRemove.keepHelp')}
                </FormHelperText>
                <FormControlLabel
                  value="RERESOLVE"
                  control={<Radio slotProps={{ input: { 'aria-describedby': rematchHelpId } }} />}
                  label={t('aliasRemove.rematch')}
                />
                <FormHelperText
                  id={rematchHelpId}
                  sx={{ mt: 0, marginInlineStart: 4, marginInlineEnd: 0 }}
                >
                  {t('aliasRemove.rematchHelp', {
                    rename: t('aliasRemove.rematchRename', { count: check.preview.wouldRename }),
                    unname: t('aliasRemove.rematchUnname', { count: check.preview.wouldUnname }),
                  })}
                </FormHelperText>
              </RadioGroup>
            </FormControl>
          )}
          {lastPattern && (
            <Typography variant="body2" data-last-pattern="">
              {t('aliasRemove.leavesOrder')}
            </Typography>
          )}
        </Stack>
      )}
    </ConfirmDialog>
  );
}
